// ProfanityWordsModal — the whole of chat.profanity_words in one dialog:
// add, remove, search, and the two directions of a CSV.
//
// It edits a DRAFT of the list and nothing else. Done hands the draft back to
// the config form, where it becomes an unsaved change like any other and leaves
// on the page's own Save; Cancel and × drop it. Nothing here talks to the
// server — a list half-saved ahead of the page would break both Discard and the
// transfer's config diff.
//
// Ports .codelane/artifacts/t-102/config-profanity-list.html.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlignLeft,
  Download,
  File as FileIcon,
  Plus,
  Search,
  Upload,
} from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Button } from "../../../components/Button/Button";
import { Input } from "../../../components/Input/Input";
import { Modal } from "../../../components/Modal/Modal";
import { Pill } from "../../../components/Pill/Pill";
import { saveBlob } from "../../../lib/download";
import {
  MAX_WORDS_FILE_BYTES,
  WORDS_FILE_TOO_BIG,
  compareWords,
  hasWord,
  normalizeWord,
  parseWordsFile,
  planWordsImport,
  problemLine,
  problemSentence,
  profanityFilename,
  readWordsFileText,
  wordCount,
  wordProblem,
  wordsCsv,
  withWord,
  type ParsedWordsFile,
  type WordsImportMode,
  type WordsImportPlan,
} from "./profanityWords";
import styles from "./ProfanityWordsModal.module.css";

const MODE_LABEL: Record<WordsImportMode, string> = {
  merge: "Merge",
  replace: "Replace",
};

const MODE_HINT: Record<WordsImportMode, string> = {
  merge: "Words already in the list are skipped; nothing is removed.",
  replace: "The list becomes exactly what the file contains.",
};

/**
 * How many skipped lines the alert names before it counts the rest. A file of
 * a thousand sentences would otherwise turn the dialog into its own error log,
 * and the first few are enough to recognise what kind of file it is.
 */
const PROBLEMS_SHOWN = 8;

interface Props {
  /** The list as the config form holds it right now. */
  words: string[];
  /** The saved list — what the row's highlight calls "added since Save". */
  serverWords: string[];
  /** Names the exported file; nothing else uses it. */
  projectName: string;
  /** Done — the draft becomes the form's value. */
  onDone: (next: string[]) => void;
  /** Cancel, × or Escape — the draft is dropped. */
  onClose: () => void;
}

type Screen = "list" | "import";

export function ProfanityWordsModal({
  words,
  serverWords,
  projectName,
  onDone,
  onClose,
}: Props) {
  const [draft, setDraft] = useState<string[]>(words);
  const [screen, setScreen] = useState<Screen>("list");

  const [addDraft, setAddDraft] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /**
   * The word to bring into view once it has a row, wrapped in a fresh object
   * per add: a word added, deleted and added again must scroll a second time,
   * and a bare string would compare equal and leave the effect asleep.
   */
  const [scrollTo, setScrollTo] = useState<{ word: string } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);

  const [picked, setPicked] = useState<{ name: string; parsed: ParsedWordsFile } | null>(
    null,
  );
  const [reading, setReading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<WordsImportMode>("merge");
  /** A file that never got as far as being parsed: too big, or unreadable. */
  const [rejected, setRejected] = useState<{ name: string; problem: string } | null>(
    null,
  );

  /**
   * The rows, alphabetical, each still carrying the position of the entry it
   * removes: the array keeps the order it was stored in, and only the reading
   * order is sorted.
   */
  const rows = useMemo(() => {
    const all = draft.map((word, index) => ({ word, index }));
    all.sort((a, b) => compareWords(a.word, b.word));
    return all;
  }, [draft]);

  const serverSet = useMemo(
    () => new Set(serverWords.map((w) => w.toLowerCase())),
    [serverWords],
  );

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? rows.filter((r) => r.word.toLowerCase().includes(needle))
    : rows;

  // The new row is on screen by the time this runs — it is the same commit that
  // set the word. Guarded because jsdom has no scrollIntoView, the way Menu and
  // AppSelector guard theirs.
  useEffect(() => {
    if (!scrollTo) return;
    const el = targetRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [scrollTo]);

  function addWord(raw: string): boolean {
    const word = normalizeWord(raw);
    // An empty box is not a mistake to report — it is somebody pressing Add
    // with nothing typed.
    if (word === "") return false;
    const problem = wordProblem(word);
    if (problem) {
      setAddError(problemSentence(problem));
      return false;
    }
    if (hasWord(draft, word)) {
      setAddError(`«${word}» is already in the list.`);
      return false;
    }
    setAddError(null);
    // `words` — the list this dialog opened on — is what a word deleted a
    // moment ago is put back into its own place by.
    setDraft((prev) => withWord(prev, word, words));
    setScrollTo({ word });
    return true;
  }

  function submitAdd() {
    if (addWord(addDraft)) setAddDraft("");
  }

  function removeAt(index: number) {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  }

  function exportCsv() {
    const blob = new Blob([wordsCsv(draft)], { type: "text/csv;charset=utf-8" });
    saveBlob(blob, profanityFilename(projectName));
  }

  async function handleFile(file: File | null | undefined) {
    if (!file || reading) return;
    // Cleared before the file is even measured: a plan belongs to the file it
    // was worked out from, and that file is no longer the one picked. Keeping
    // it would put a plan and a refusal about two different files on one
    // screen — ImportRewardsModal drops its own for the same reason.
    setPicked(null);
    setRejected(null);
    // A new file is planned in the mode the screen opens in — Replace is not
    // something to inherit from the last one.
    setMode("merge");
    setReading(true);
    try {
      if (file.size > MAX_WORDS_FILE_BYTES) {
        setRejected({ name: file.name, problem: WORDS_FILE_TOO_BIG });
        return;
      }
      let text: string;
      try {
        text = await readWordsFileText(file);
      } catch {
        setRejected({
          name: file.name,
          problem: "Couldn't read this file. Try picking it again.",
        });
        return;
      }
      setPicked({ name: file.name, parsed: parseWordsFile(text) });
    } finally {
      setReading(false);
    }
  }

  function applyImport(plan: WordsImportPlan) {
    setDraft(plan.result);
    setScreen("list");
    // A filter left over from before the import would hide most of what just
    // arrived.
    setQuery("");
    setAddError(null);
  }

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept=".csv,.txt,text/csv,text/plain"
      className={styles.fileInput}
      aria-label="Word list file"
      onChange={(e) => {
        const file = e.target.files?.[0];
        // Reset so picking the same file twice fires change again.
        e.target.value = "";
        void handleFile(file);
      }}
    />
  );

  const doneFooter = (
    <>
      <span className={styles.footerNote}>
        Nothing is saved until you press Save on the page.
      </span>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={() => onDone(draft)}>
        Done
      </Button>
    </>
  );

  // === Import ========================================================= //
  if (screen === "import") {
    const parsed = picked?.parsed ?? null;
    const plan = parsed ? planWordsImport(draft, parsed, mode) : null;
    const empty = parsed !== null && parsed.words.length === 0;
    const nothingToAdd = plan !== null && mode === "merge" && plan.add.length === 0;

    return (
      <Modal
        open
        onClose={onClose}
        title="Import words"
        width="lg"
        footer={
          <>
            <div className={styles.footerLeft}>
              <Button variant="secondary" onClick={() => setScreen("list")}>
                Back
              </Button>
            </div>
            <Button
              variant="primary"
              disabled={!plan || empty || nothingToAdd}
              onClick={() => plan && applyImport(plan)}
            >
              {importLabel(plan, mode, empty || nothingToAdd)}
            </Button>
          </>
        }
      >
        {rejected && (
          <div className={styles.alertTop}>
            <Alert variant="danger" title={`${rejected.name} was not read`}>
              {rejected.problem}
            </Alert>
          </div>
        )}

        {!picked ? (
          <>
            <div
              className={
                dragOver
                  ? `${styles.dropzone} ${styles.dropzoneOver}`
                  : styles.dropzone
              }
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void handleFile(e.dataTransfer?.files?.[0]);
              }}
            >
              <span className={styles.dzIcon} aria-hidden="true">
                <Upload size={20} />
              </span>
              <span className={styles.dzTitle}>Drop a word list here</span>
              <span className={styles.dzOr}>or</span>
              <Button
                variant="secondary"
                size="sm"
                loading={reading}
                onClick={() => fileRef.current?.click()}
              >
                Choose a file
              </Button>
              <span className={styles.dzHint}>.csv · .txt · up to 1 MB</span>
            </div>
            <p className={styles.dzHelp}>
              One word per line. A line with commas is split on them, so a
              single-line list works too. Words are lowercased; anything already
              in the list is skipped.
            </p>
          </>
        ) : (
          <>
            <div className={styles.fileBar}>
              <span className={styles.fileIcon} aria-hidden="true">
                <FileIcon size={16} />
              </span>
              <div className={styles.fileCol}>
                <div className={styles.fileName}>{picked.name}</div>
                <div className={styles.fileMeta}>
                  {plural(picked.parsed.linesRead, "line", "lines")} read ·{" "}
                  {wordCount(picked.parsed.words.length)} after cleanup
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                Replace file
              </Button>
            </div>

            <div className={styles.modeRow}>
              <div className={styles.seg} role="radiogroup" aria-label="Import mode">
                {(["merge", "replace"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={mode === m}
                    className={`${styles.segOpt} ${
                      mode === m ? styles.segOptActive : ""
                    }`}
                    onClick={() => setMode(m)}
                  >
                    {MODE_LABEL[m]}
                  </button>
                ))}
              </div>
              <p className={styles.modeHint}>{MODE_HINT[mode]}</p>
            </div>

            {/* A file with nothing in it is a file picked by mistake, not a
                plan to empty the list — so it is answered instead of planned.
                Drawing "814 remove" beside a button that refuses to press
                would be the dialog contradicting itself. */}
            {empty && (
              <div className={styles.alertSlot}>
                <Alert variant="danger" title="No words in this file">
                  Nothing in it passed as a word, so there is nothing to import
                  — check you picked the right file.
                </Alert>
              </div>
            )}

            {plan && !empty && (
              <>
                <div className={styles.summary}>
                  <span className={styles.summaryLabel}>Plan</span>
                  {planPills(plan).map((p) => (
                    <Pill key={p.text} variant={p.variant}>
                      {p.text}
                    </Pill>
                  ))}
                </div>

                {mode === "replace" && plan.remove.length > 0 && (
                  <div className={styles.alertSlot}>
                    <Alert
                      variant="danger"
                      title={`${wordCount(plan.remove.length)} will be dropped`}
                    >
                      They are in the list now and not in this file. The change
                      is still undoable — nothing leaves the browser until you
                      press Save on the config page.
                    </Alert>
                  </div>
                )}
              </>
            )}

            {/* Outside the plan block on purpose: a file every line of which
                was turned away has no plan to show, and the reasons are then
                the only thing there is to say about it. */}
            {plan && plan.skipped.length > 0 && (
              <div className={styles.alertSlot}>
                <Alert
                  variant="warn"
                  title={`${plural(plan.skipped.length, "line", "lines")} skipped`}
                >
                  <ul className={styles.problems}>
                    {plan.skipped.slice(0, PROBLEMS_SHOWN).map((p) => (
                      <li key={`${p.line}-${p.raw}`}>{problemLine(p)}</li>
                    ))}
                  </ul>
                  {plan.skipped.length > PROBLEMS_SHOWN &&
                    `…and ${plan.skipped.length - PROBLEMS_SHOWN} more.`}
                </Alert>
              </div>
            )}
          </>
        )}
        {fileInput}
      </Modal>
    );
  }

  // === The list ======================================================= //
  return (
    <Modal
      open
      onClose={onClose}
      title="Profanity words"
      width="lg"
      footer={
        <>
          <div className={styles.footerLeft}>
            <Button
              variant="secondary"
              icon={<Upload size={14} />}
              onClick={() => setScreen("import")}
            >
              Import…
            </Button>
            {/* An empty list has no file to offer. The button is left out
                rather than greyed: a disabled Export promises a file that
                cannot exist. */}
            {draft.length > 0 && (
              <Button
                variant="secondary"
                icon={<Download size={14} />}
                onClick={exportCsv}
              >
                Export CSV
              </Button>
            )}
          </div>
          {doneFooter}
        </>
      }
    >
      <div className={`${styles.bar} ${styles.barAdd}`}>
        <div className={styles.barGrow}>
          <Input
            aria-label="Add a word"
            placeholder="Add a word…"
            value={addDraft}
            error={addError ?? undefined}
            onChange={(e) => {
              setAddDraft(e.target.value);
              if (addError) setAddError(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              submitAdd();
            }}
          />
        </div>
        <Button
          variant="primary"
          icon={<Plus size={14} />}
          className={styles.addButton}
          onClick={submitAdd}
        >
          Add
        </Button>
      </div>

      {draft.length > 0 && (
        <div className={styles.bar}>
          <div className={styles.barGrow}>
            <Input
              aria-label="Search words"
              placeholder="Search words…"
              value={query}
              leadingIcon={<Search size={14} />}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <span className={styles.count}>
            {needle
              ? `${visible.length} of ${draft.length}`
              : wordCount(draft.length)}
          </span>
        </div>
      )}

      {draft.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon} aria-hidden="true">
            <AlignLeft size={22} />
          </div>
          <div className={styles.emptyTitle}>No custom words</div>
          <div className={styles.emptyDesc}>
            The platform&apos;s built-in list still applies.
            <br />
            Add words here to extend it for this project.
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setScreen("import")}
          >
            Import a CSV file
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <div className={`${styles.empty} ${styles.emptySmall}`}>
          <div className={styles.emptyTitle}>
            No word matches “{query.trim()}”
          </div>
          <div className={styles.emptyDesc}>{noMatchNote(draft.length)}</div>
          {/* Offered only when the text could BE a word: a button that would
              answer with "a word cannot contain spaces" is not an offer. */}
          {wordProblem(normalizeWord(query)) === null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => addWord(query)}
            >
              Add “{query.trim()}” to the list
            </Button>
          )}
        </div>
      ) : (
        <div className={styles.list}>
          {visible.map((r) => {
            const isNew = !serverSet.has(r.word.toLowerCase());
            return (
              <div
                key={r.index}
                ref={r.word === scrollTo?.word ? targetRef : undefined}
                className={`${styles.row} ${isNew ? styles.rowNew : ""}`}
              >
                <span>{r.word}</span>
                <button
                  type="button"
                  className={styles.remove}
                  aria-label={`Remove ${r.word}`}
                  onClick={() => removeAt(r.index)}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Pieces
// ──────────────────────────────────────────────────────────────────────────────

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The one-line explanation under "No word matches …". */
function noMatchNote(total: number): string {
  if (total === 1) return "The only word in the list does not contain this text.";
  return `The list has ${total} words — none of them contains this text.`;
}

/**
 * The counts that are not zero, in the order the mockup lists them. Merge
 * names what the file brings; Replace also names what it takes away, so the
 * two modes print different strips off the same plan.
 */
interface PlanPill {
  n: number;
  text: string;
  variant: "success" | "danger" | "neutral" | "warn";
}

function planPills(plan: WordsImportPlan): PlanPill[] {
  const pills: PlanPill[] = [
    { n: plan.add.length, text: `${plan.add.length} add`, variant: "success" },
  ];
  if (plan.mode === "replace") {
    pills.push(
      { n: plan.remove.length, text: `${plan.remove.length} remove`, variant: "danger" },
      { n: plan.kept.length, text: `${plan.kept.length} kept`, variant: "neutral" },
    );
  } else {
    pills.push({
      n: plan.alreadyThere.length,
      text: `${plan.alreadyThere.length} already there`,
      variant: "neutral",
    });
  }
  pills.push({
    n: plan.skipped.length,
    text: `${plan.skipped.length} skipped`,
    variant: "warn",
  });
  return pills.filter((p) => p.n > 0);
}

/**
 * The confirm button's text — it names the result rather than the action, so
 * the number the operator just read on the plan strip is the number they press.
 */
function importLabel(
  plan: WordsImportPlan | null,
  mode: WordsImportMode,
  nothing: boolean,
): string {
  if (!plan) return mode === "merge" ? "Import" : "Replace";
  if (nothing) return mode === "merge" ? "Nothing to add" : "Nothing to import";
  if (mode === "merge") return `Import ${wordCount(plan.add.length)}`;
  return `Replace — ${wordCount(plan.result.length)}`;
}
